import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { getValueFromEntityInstancePath, mergedPaths } from "./pathHelpers";
import { isDocument } from "./typeGuardUtils";

export enum DisplayType {
	SHOW = 0,
	DISABLE = 1,
	HIDE = 2
}

interface DependentEnablement {
	path: ModelPath;
	display: "hide" | "disable";
	negation: boolean;
}

export function resolveDisplayType(
	document: object,
	screenPath: EntityInstancePath,
	annotationValue: string
): DisplayType {
	return annotationValue
		.split("|")
		.map(dependentEnablementString => resolveDependentEnablement(document, screenPath, dependentEnablementString))
		.filter((e): e is DisplayType => !!e)
		.reduce((e1, e2) => (e1.valueOf() >= e2.valueOf() ? e1 : e2), DisplayType.SHOW);
}

export function resolveDependentEnablement(
	document: object,
	screenPath: EntityInstancePath,
	dependentEnablementString: string
) {
	const properties = dependentEnablementString.split(",");
	if (properties.length == 1) {
		// defaults: display = Hide
		const path = ModelPath.fromString(dependentEnablementString).slice(1);
		if (evaluateEnumeration(document, screenPath, path, false)) {
			return DisplayType.HIDE;
		} else {
			return DisplayType.SHOW;
		}
	}

	const dependentEnablement = parseToDependentEnablement(properties);
	if (!dependentEnablement) {
		console.error("Invalid Annotation: " + dependentEnablementString);
		return DisplayType.SHOW;
	}

	if (!evaluateEnumeration(document, screenPath, dependentEnablement.path, dependentEnablement.negation)) {
		return DisplayType.SHOW;
	}

	return dependentEnablement.display === "hide" ? DisplayType.HIDE : DisplayType.DISABLE;
}

function parseToDependentEnablement(properties: string[]): DependentEnablement | undefined {
	let path: ModelPath | undefined = undefined;
	let display: "hide" | "disable" = "hide";
	let negation = false;

	for (const property of properties.map(e => e.split("="))) {
		if (property.length != 2) {
			console.error("Invalid properties: " + properties);
			return undefined;
		}

		switch (property[0]) {
			case "path":
				path = ModelPath.fromString(property[1]).slice(1);
				break;
			case "negation":
				negation = property[1] == "true";
				break;
			case "display":
				display = property[1] === "hide" || property[1] === "disable" ? property[1] : "hide";
		}
	}

	if (!path) {
		console.error("Invalid properties: " + properties);
		return undefined;
	}

	return {
		path,
		display,
		negation
	};
}

function evaluateEnumeration(
	document: object,
	screenPath: EntityInstancePath,
	path: ModelPath,
	negation: boolean
): boolean {
	const entityInstancePath: EntityInstancePath = mergedPaths(path, screenPath);
	const value = isDocument(document) ? getValueFromEntityInstancePath(entityInstancePath, document) : undefined;
	const hasValue = value !== undefined && value !== null && value !== "";
	return negation !== hasValue;
}
